import {
  ROTULO_FAIXA_ESPERA,
  type Dashboard,
  type LinhaRankingVelocidade,
} from '@baiocchi/shared'
import {
  Abas,
  Avatar,
  BarraSegmentada,
  Botao,
  CabecalhoCartao,
  Cartao,
  CartaoIndicador,
  CorpoCartao,
  Esqueleto,
  EstadoVazio,
  Etiqueta,
  Rosca,
  TabelaDados,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../api/chaves'
import { indicadoresApi } from '../api/indicadores'
import { EstadoDeErro } from '../componentes/estados-tela'
import { EtiquetaStatus } from '../componentes/etiquetas-dominio'
import { FiltroPeriodo } from '../componentes/filtro-periodo'
import { IconeAgenda, IconeCheck, IconeRelogio } from '../componentes/icones'
import { COR_FAIXA_ESPERA } from '../dominio/rotulos'
import { urlDoDrilldown } from '../dominio/drilldown'
import { useFiltrosUrl } from '../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../layout/layout-app'
import {
  formatarDataHora,
  formatarDinheiro,
  formatarDuracaoCurta,
  formatarNumero,
  formatarTempoRelativo,
} from '../utilitarios/formatar'

/**
 * TELA-01 — Dashboard.
 *
 * Blocos do MVP: Recebidos, Aguardando, Em atendimento, Leads Finalizados,
 * Agendamentos e Leads Recentes. Vendas e Conversão vêm da mesma resposta (a API
 * as calcula de todo modo) e aparecem marcadas como **Fase 2** — esconder um
 * número que o banco já sabe calcular não deixaria a tela mais honesta, mas
 * exibi-lo sem a etiqueta faria a operação cobrar um relatório de vendas que ainda
 * não existe.
 *
 * Tudo respeita o escopo do usuário, aplicado no servidor: o atendente vê o
 * próprio recorte sem que a tela peça nada diferente.
 */
export function PaginaDashboard() {
  const { filtros, definir } = useFiltrosUrl()
  const navegar = useNavigate()

  const consultaFiltros = {
    de: filtros.de,
    ate: filtros.ate,
    unidadeId: filtros.unidadeId,
    equipeId: filtros.equipeId,
    responsavelUuid: filtros.responsavelUuid,
    pontoConversaoId: filtros.pontoConversaoId,
  }

  const consulta = useQuery({
    queryKey: chaves.indicadores.dashboard(consultaFiltros),
    queryFn: ({ signal }) => indicadoresApi.dashboard(consultaFiltros, signal),
    staleTime: 60_000,
  })

  const dados = consulta.data?.dados
  const meta = consulta.data?.meta

  return (
    <>
      <CabecalhoPagina
        titulo="Início"
        descricao="Visão do período no seu escopo de acesso."
        acoes={
          meta ? (
            <span className="text-xs text-conteudo-fraco">
              Atualizado {formatarTempoRelativo(meta.atualizadoEm)}
              {meta.defasagemMinutos > 0 ? ` · defasagem de ${meta.defasagemMinutos} min` : ''}
            </span>
          ) : null
        }
      />

      <Cartao className="mb-5">
        <FiltroPeriodo
          de={filtros.de}
          ate={filtros.ate}
          onAlterar={(periodo) => definir(periodo)}
        />
      </Cartao>

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-5">
          {/* Linha 1 — os três donuts do MVP */}
          <div className="grid gap-4 lg:grid-cols-3">
            <BlocoRosca
              titulo="Recebidos"
              carregando={consulta.isPending}
              total={dados?.recebidos.total ?? 0}
              legenda="leads no período"
              fatias={(dados?.recebidos.porPontoConversao ?? []).map((fatia) => ({
                rotulo: fatia.rotulo,
                valor: fatia.total,
                percentual: fatia.percentual,
                onSelecionar: () =>
                  navegar(
                    urlDoDrilldown({
                      ...dados?.recebidos.drilldown,
                      ...(fatia.id !== null ? { pontoConversaoId: String(fatia.id) } : {}),
                    }),
                  ),
              }))}
              segmentos={(dados?.recebidos.porUnidade ?? []).map((fatia) => ({
                rotulo: fatia.rotulo,
                valor: fatia.total,
                percentual: fatia.percentual,
                onSelecionar: () =>
                  navegar(
                    urlDoDrilldown({
                      ...dados?.recebidos.drilldown,
                      ...(fatia.id !== null ? { unidadeId: String(fatia.id) } : {}),
                    }),
                  ),
              }))}
              aoClicarCentro={() => navegar(urlDoDrilldown(dados?.recebidos.drilldown))}
            />

            <BlocoAguardando dados={dados} carregando={consulta.isPending} />

            <BlocoRosca
              titulo="Em atendimento"
              carregando={consulta.isPending}
              total={dados?.emAtendimento.total ?? 0}
              legenda="leads em atendimento"
              fatias={(dados?.emAtendimento.porPontoConversao ?? []).map((fatia) => ({
                rotulo: fatia.rotulo,
                valor: fatia.total,
                percentual: fatia.percentual,
                onSelecionar: () =>
                  navegar(
                    urlDoDrilldown({
                      ...dados?.emAtendimento.drilldown,
                      ...(fatia.id !== null ? { pontoConversaoId: String(fatia.id) } : {}),
                    }),
                  ),
              }))}
              segmentos={(dados?.emAtendimento.porUnidade ?? []).map((fatia) => ({
                rotulo: fatia.rotulo,
                valor: fatia.total,
                percentual: fatia.percentual,
              }))}
              aoClicarCentro={() => navegar(urlDoDrilldown(dados?.emAtendimento.drilldown))}
            />
          </div>

          {/* Linha 2 — cartões numéricos */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <CartaoIndicador
              rotulo="Leads finalizados"
              valor={formatarNumero(dados?.leadsFinalizados.total ?? 0)}
              icone={<IconeCheck />}
              carregando={consulta.isPending}
              onSelecionar={() => navegar(urlDoDrilldown(dados?.leadsFinalizados.drilldown))}
            />

            <CartaoIndicador
              rotulo="Agendamentos"
              valor={formatarNumero(dados?.agendamentos.total ?? 0)}
              icone={<IconeAgenda />}
              carregando={consulta.isPending}
              descricao={
                dados && dados.agendamentos.vencidos > 0 ? (
                  <span className="font-medium text-perigo">
                    {dados.agendamentos.vencidos} vencido(s) sem tratativa
                  </span>
                ) : (
                  'Compromissos no período'
                )
              }
              onSelecionar={() => navegar('/agendamentos')}
            />

            <CartaoIndicador
              rotulo="Vendas realizadas"
              valor={formatarNumero(dados?.vendas.total ?? 0)}
              tom="sucesso"
              carregando={consulta.isPending}
              descricao={
                <span className="inline-flex items-center gap-1.5">
                  <Etiqueta tom="neutro">Fase 2</Etiqueta>
                  {dados?.vendas.valorTotal
                    ? `Total ${formatarDinheiro(dados.vendas.valorTotal)}`
                    : null}
                </span>
              }
              onSelecionar={() => navegar(urlDoDrilldown(dados?.vendas.drilldown))}
            />

            <CartaoIndicador
              rotulo="Taxa de conversão"
              valor={`${(dados?.conversao.taxa ?? 0).toLocaleString('pt-BR', {
                maximumFractionDigits: 1,
              })}%`}
              tom="info"
              carregando={consulta.isPending}
              descricao={
                <span className="inline-flex items-center gap-1.5">
                  <Etiqueta tom="neutro">Fase 2</Etiqueta>
                  {dados
                    ? `${formatarNumero(dados.conversao.vendas)} de ${formatarNumero(
                        dados.conversao.recebidos,
                      )}`
                    : null}
                </span>
              }
              onSelecionar={() => navegar('/relatorios/funil')}
            />
          </div>

          {/* Linha 3 — ranking de velocidade de atendimento */}
          <BlocoRankingVelocidadeAtendimento dados={dados} carregando={consulta.isPending} />

          {/* Linha 4 — leads recentes */}
          <Cartao semPadding>
            <CabecalhoCartao
              titulo="Leads recentes"
              descricao="Últimos leads que entraram no seu escopo."
              acoes={
                <Botao variante="contorno" tamanho="sm" onClick={() => navegar('/leads')}>
                  Ver todos
                </Botao>
              }
            />
            {consulta.isPending ? (
              <CorpoCartao className="space-y-2">
                {Array.from({ length: 4 }, (_, indice) => (
                  <Esqueleto key={indice} className="h-16 w-full" />
                ))}
              </CorpoCartao>
            ) : (dados?.leadsRecentes.length ?? 0) === 0 ? (
              <EstadoVazio
                titulo="Nenhum lead no período"
                descricao="Ajuste o período ou cadastre um lead para começar."
                acao={
                  <Botao onClick={() => navegar('/leads/novo')}>Cadastrar lead</Botao>
                }
              />
            ) : (
              <ul className="divide-y divide-borda">
                {dados?.leadsRecentes.map((lead) => (
                  <li key={lead.uuid}>
                    <Link
                      to={`/leads/${lead.uuid}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 transition-colors hover:bg-superficie-2 sm:px-5"
                    >
                      <EtiquetaStatus status={lead.status} />
                      <span className="min-w-0 flex-1 truncate font-medium text-conteudo">
                        {lead.clienteNome}
                      </span>
                      {lead.pontoConversao ? (
                        <Etiqueta tom="marca">{lead.pontoConversao.titulo}</Etiqueta>
                      ) : null}
                      <Etiqueta tom="neutro">{lead.unidade.titulo}</Etiqueta>
                      <span className="text-xs text-conteudo-suave">
                        {lead.responsavel?.nome ?? 'Sem responsável'}
                      </span>
                      <span className="w-full text-xs tabular-nums text-conteudo-fraco sm:w-auto">
                        {formatarDataHora(lead.chegadaEm)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>

          <p className="text-xs leading-relaxed text-conteudo-fraco">
            Os blocos <strong>Qualidade</strong> e <strong>Últimas avaliações negativas</strong> do
            sistema legado dependem do módulo de avaliação do cliente, previsto para a Fase 3 — a
            coleta não existe hoje, e exibir uma nota sem origem seria inventar dado.
          </p>
        </div>
      )}
    </>
  )
}

function BlocoRosca({
  titulo,
  total,
  legenda,
  fatias,
  segmentos,
  carregando,
  aoClicarCentro,
}: {
  titulo: string
  total: number
  legenda: string
  fatias: ReadonlyArray<{
    rotulo: string
    valor: number
    percentual?: number
    onSelecionar?: () => void
  }>
  segmentos: ReadonlyArray<{
    rotulo: string
    valor: number
    percentual?: number
    onSelecionar?: () => void
  }>
  carregando: boolean
  aoClicarCentro: () => void
}) {
  return (
    <Cartao semPadding className="flex flex-col">
      <CabecalhoCartao titulo={titulo} />
      <CorpoCartao className="flex flex-1 flex-col gap-4">
        {carregando ? (
          <div className="flex flex-col items-center gap-4">
            <Esqueleto className="h-[168px] w-[168px] rounded-full" />
            <Esqueleto className="h-16 w-full" />
          </div>
        ) : (
          <>
            <Rosca
              fatias={fatias}
              centro={formatarNumero(total)}
              legendaCentro={legenda}
              onSelecionarCentro={aoClicarCentro}
            />
            <div className="mt-auto">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-conteudo-fraco">
                Por unidade
              </p>
              <BarraSegmentada segmentos={segmentos} />
            </div>
          </>
        )}
      </CorpoCartao>
    </Cartao>
  )
}

/**
 * "Ranking de velocidade de atendimento" — quem responde mais rápido.
 *
 * Mede `primeiro atendimento humano − chegada do lead` e credita **quem atendeu**
 * (autor da interação), não quem é o responsável atual do lead: o domínio separa os
 * dois conceitos, e o lead transferido depois do primeiro contato continua contando
 * para quem fez o contato.
 *
 * Respeita o filtro de período da tela pelo **mesmo** recorte de "Recebidos":
 * `chegada_em` dentro do intervalo. Um lead que chegou em 31/07 e foi atendido em
 * 01/08 é de julho — senão o ranking e o número de leads recebidos falariam de bases
 * diferentes na mesma tela.
 *
 * Sem drill-down, ao contrário dos outros blocos: `GET /leads` filtra por
 * responsável **atual**, e clicar aqui abriria uma lista diferente do número
 * mostrado. Ver a nota de `BlocoRankingVelocidade` no contrato.
 */
export function BlocoRankingVelocidadeAtendimento({
  dados,
  carregando,
}: {
  dados: Dashboard | undefined
  carregando: boolean
}) {
  const grupos = dados?.rankingVelocidade.grupos ?? []
  const minimo = dados?.rankingVelocidade.minimoLeadsAtendidos ?? 0

  // `null` = ainda não houve escolha do usuário; a aba vem do padrão. Guardar a
  // escolha em vez de derivá-la o tempo todo é o que impede a aba de pular sozinha
  // quando o período muda e outro departamento passa a ter mais volume.
  const [escolhida, escolher] = useState<number | null>(null)
  const ativa = grupos.some((grupo) => grupo.unidade.id === escolhida)
    ? (escolhida as number)
    : (unidadePadrao(grupos) ?? 0)

  const grupo = grupos.find((item) => item.unidade.id === ativa)

  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo="🏆 Ranking de velocidade de atendimento"
        descricao="Quem responde mais rapidamente aos leads que chegaram no período."
      />

      {carregando ? (
        <CorpoCartao className="space-y-2">
          {Array.from({ length: 4 }, (_, indice) => (
            <Esqueleto key={indice} className="h-11 w-full" />
          ))}
        </CorpoCartao>
      ) : grupos.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum atendimento no período."
          descricao="O ranking aparece assim que o primeiro atendimento humano for registrado."
        />
      ) : (
        <>
          {/* Uma aba por unidade — Vendas, Aluguel. O contador é quantos entraram
              no ranking daquela unidade, não quantos leads: é o que a aba mostra. */}
          <Abas
            itens={grupos.map((item) => ({
              valor: String(item.unidade.id),
              rotulo: item.unidade.titulo,
              contador: item.linhas.length,
            }))}
            ativa={String(ativa)}
            onAlterar={(valor) => escolher(Number(valor))}
          />

          {grupo === undefined || grupo.linhas.length === 0 ? (
            <EstadoVazio
              titulo="Nenhum atendimento no período."
              descricao={
                grupo && grupo.abaixoDoMinimo > 0
                  ? `${grupo.abaixoDoMinimo} atendente(s) com menos de ${minimo} leads atendidos ainda não entram no ranking.`
                  : 'Nenhum atendimento humano foi registrado nesta unidade no período selecionado.'
              }
            />
          ) : (
            <TabelaDados
              colunas={COLUNAS_RANKING}
              itens={grupo.linhas}
              chaveDe={(linha) => linha.usuario.uuid}
              cartaoMobile={(linha) => <CartaoRanking linha={linha} />}
              rodape={
                grupo.abaixoDoMinimo > 0 ? (
                  <span className="text-xs text-conteudo-fraco">
                    {grupo.abaixoDoMinimo} atendente(s) com menos de {minimo} leads
                    atendidos ficaram de fora — com pouco volume, a média não compara.
                  </span>
                ) : null
              }
            />
          )}
        </>
      )}
    </Cartao>
  )
}

/**
 * Aba aberta por padrão: a unidade com mais leads atendidos no período.
 *
 * Não é a primeira da ordem do catálogo, e não é um título fixo no código. "Abrir em
 * Vendas" é o que a operação quer, mas gravar a string `'Vendas'` aqui quebraria no
 * dia em que alguém renomeasse a unidade em Configurações — e o volume entrega o
 * mesmo resultado por um caminho que continua verdadeiro depois da renomeação.
 *
 * Empate ou nenhum dado: a primeira da ordem do catálogo, que é determinístico.
 */
function unidadePadrao(
  grupos: Dashboard['rankingVelocidade']['grupos'],
): number | undefined {
  if (grupos.length === 0) return undefined

  const volumeDe = (grupo: (typeof grupos)[number]): number =>
    grupo.linhas.reduce((soma, linha) => soma + linha.leadsAtendidos, 0)

  return grupos.reduce((melhor, atual) =>
    volumeDe(atual) > volumeDe(melhor) ? atual : melhor,
  ).unidade.id
}

const MEDALHAS: Readonly<Record<number, string>> = { 1: '🥇', 2: '🥈', 3: '🥉' }

/**
 * Medalha **e** número, nos três primeiros.
 *
 * A medalha sozinha não serve: no tamanho de corpo da tabela, 🥇🥈🥉 diferem só
 * pela cor e ficam indistinguíveis — mesma armadilha que RNF-119 descreve para o
 * badge de status. O número é o canal que sempre funciona; o emoji é o realce.
 */
function Posicao({ posicao }: { posicao: number }) {
  const medalha = MEDALHAS[posicao]
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      {medalha ? <span aria-hidden="true">{medalha}</span> : null}
      <span className={medalha ? 'font-semibold text-conteudo' : 'font-medium text-conteudo-suave'}>
        {posicao}º
      </span>
    </span>
  )
}

const COLUNAS_RANKING: ReadonlyArray<ColunaTabela<LinhaRankingVelocidade>> = [
  {
    chave: 'posicao',
    cabecalho: '#',
    classe: 'w-20',
    celula: (linha) => <Posicao posicao={linha.posicao} />,
  },
  {
    chave: 'atendente',
    cabecalho: 'Atendente',
    celula: (linha) => (
      <span className="inline-flex min-w-0 items-center gap-2">
        <Avatar nome={linha.usuario.nome} fotoUrl={linha.usuario.fotoUrl} tamanho="sm" />
        <span className="truncate font-medium text-conteudo">{linha.usuario.nome}</span>
      </span>
    ),
  },
  {
    chave: 'leadsAtendidos',
    cabecalho: 'Leads atend.',
    alinhamento: 'direita',
    classe: 'w-32 tabular-nums',
    celula: (linha) => formatarNumero(linha.leadsAtendidos),
  },
  {
    chave: 'tempoMedio',
    cabecalho: 'Tempo médio',
    alinhamento: 'direita',
    classe: 'w-36 tabular-nums',
    celula: (linha) => (
      <span className="font-medium text-conteudo">
        {formatarDuracaoCurta(linha.tempoMedioSegundos)}
      </span>
    ),
  },
]

function CartaoRanking({ linha }: { linha: LinhaRankingVelocidade }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-borda px-3 py-2.5">
      <Posicao posicao={linha.posicao} />
      <Avatar nome={linha.usuario.nome} fotoUrl={linha.usuario.fotoUrl} tamanho="sm" />
      <span className="min-w-0 flex-1 truncate font-medium text-conteudo">
        {linha.usuario.nome}
      </span>
      <span className="text-right text-xs tabular-nums text-conteudo-fraco">
        {formatarNumero(linha.leadsAtendidos)} lead(s)
        <br />
        <span className="font-medium text-conteudo">
          {formatarDuracaoCurta(linha.tempoMedioSegundos)}
        </span>
      </span>
    </div>
  )
}

/**
 * Bloco "Aguardando" — leads sem primeiro atendimento.
 *
 * **Ignora o filtro de período de propósito** (`05-dashboard-metrics.md §4`): é o
 * bloco que responde "o que está queimando agora", e um lead que chegou há três
 * dias e ninguém atendeu precisa aparecer mesmo com o filtro em "hoje". O aviso na
 * tela existe para o usuário não achar que o número está errado.
 *
 * As faixas usam a paleta de **estado** (bom → crítico), não a categórica: a
 * escala é de urgência e é ordenada. Cada faixa traz rótulo e número, então a cor
 * nunca é o único canal.
 */
function BlocoAguardando({
  dados,
  carregando,
}: {
  dados: Dashboard | undefined
  carregando: boolean
}) {
  const navegar = useNavigate()
  const aguardando = dados?.aguardando

  return (
    <Cartao semPadding className="flex flex-col">
      <CabecalhoCartao
        titulo="Aguardando primeiro atendimento"
        descricao="Independe do período selecionado — é a fila de agora."
      />
      <CorpoCartao className="flex flex-1 flex-col gap-4">
        {carregando ? (
          <div className="flex flex-col items-center gap-4">
            <Esqueleto className="h-[168px] w-[168px] rounded-full" />
            <Esqueleto className="h-16 w-full" />
          </div>
        ) : (
          <>
            <Rosca
              fatias={(aguardando?.faixas ?? [])
                .filter((faixa) => faixa.total > 0)
                .map((faixa) => ({
                  rotulo: faixa.rotulo || ROTULO_FAIXA_ESPERA[faixa.faixa],
                  valor: faixa.total,
                  cor: COR_FAIXA_ESPERA[faixa.faixa],
                  onSelecionar: () => navegar(urlDoDrilldown(faixa.drilldown)),
                }))}
              centro={formatarNumero(aguardando?.total ?? 0)}
              legendaCentro="sem primeiro atendimento"
              onSelecionarCentro={() => navegar(urlDoDrilldown(aguardando?.drilldown))}
            />

            <div className="mt-auto">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-conteudo-fraco">
                Tempo de espera
              </p>
              <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {(aguardando?.faixas ?? []).map((faixa) => (
                  <li key={faixa.faixa}>
                    <button
                      type="button"
                      onClick={() => navegar(urlDoDrilldown(faixa.drilldown))}
                      className="flex w-full flex-col items-center gap-0.5 rounded-md border border-borda px-2 py-1.5 transition-colors hover:bg-superficie-2"
                    >
                      <span
                        aria-hidden="true"
                        className="h-1 w-full rounded-full"
                        style={{ background: COR_FAIXA_ESPERA[faixa.faixa] }}
                      />
                      <span className="text-base font-semibold tabular-nums text-conteudo">
                        {faixa.total}
                      </span>
                      <span className="text-[0.625rem] leading-tight text-conteudo-fraco">
                        {faixa.rotulo || ROTULO_FAIXA_ESPERA[faixa.faixa]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 inline-flex items-center gap-1 text-[0.6875rem] text-conteudo-fraco">
                <IconeRelogio className="h-3.5 w-3.5" />
                Contado da chegada do lead até agora.
              </p>
            </div>
          </>
        )}
      </CorpoCartao>
    </Cartao>
  )
}
